import { useCallback } from 'react';
import { useJobs } from '../contexts/JobContext';

/**
 * Thin wrapper around the global JobContext so existing page code needs no changes.
 * Jobs registered here survive page navigation — the global polling loop in JobProvider
 * keeps running and the JobStatusBar shows live status.
 */
export function useAsyncJob() {
  const { registerJob, subscribeToJob } = useJobs();

  /**
   * Register jobId with the global context and return a Promise that resolves
   * with job.result when the backend job completes.
   *
   * @param {string} jobId
   * @param {function} [onStatusUpdate] - called each poll with the raw job object
   * @param {string} [label] - human-readable label shown in JobStatusBar
   * @param {object} [meta] - optional job metadata
   */
  const startPolling = useCallback((jobId, onStatusUpdate, label = 'AI Processing…', meta = {}) => {
    let unsubscribe = null;
    if (onStatusUpdate) {
      unsubscribe = subscribeToJob(jobId, onStatusUpdate);
    }

    return registerJob(jobId, label, meta)
      .then(result => {
        if (unsubscribe) unsubscribe();
        return result;
      })
      .catch(err => {
        if (unsubscribe) unsubscribe();
        throw err;
      });
  }, [registerJob, subscribeToJob]);

  const stopPolling = useCallback(() => {
    // No-op — the global loop manages its own lifecycle
  }, []);

  return { polling: false, startPolling, stopPolling };
}
